import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  // Always include Content-Type so axios + FastAPI parse JSON bodies correctly.
  // Without it, some POST calls send raw body and FastAPI returns 422 (model_attributes_type).
  const base = { "Content-Type": "application/json" };
  return token ? { ...base, Authorization: `Bearer ${token}` } : base;
};

export { getHeaders };

export const api = {
  // Auth
  initAdmin: () => axios.post(`${API_URL}/init-admin`),
  forgotPassword: (email) => axios.post(`${API_URL}/auth/forgot-password`, { email }),
  resetPassword: (token, new_password) => axios.post(`${API_URL}/auth/reset-password`, { token, new_password }),
  resendTwoFactorCode: (challenge_id) => axios.post(`${API_URL}/auth/2fa/resend`, { challenge_id }),
  startEnableTwoFactor: (current_password, channel = "email") =>
    axios.post(`${API_URL}/auth/2fa/enable/start`, { current_password, channel }, { headers: getHeaders() }),
  updateMyPhone: (phone) => axios.put(`${API_URL}/auth/me/phone`, { phone }, { headers: getHeaders() }),
  getSmsStatus: () => axios.get(`${API_URL}/sms/status`, { headers: getHeaders() }),
  sendTestSms: () => axios.post(`${API_URL}/sms/test`, {}, { headers: getHeaders() }),
  confirmEnableTwoFactor: (challenge_id, code) =>
    axios.post(`${API_URL}/auth/2fa/enable/confirm`, { challenge_id, code }, { headers: getHeaders() }),
  disableTwoFactor: (current_password) =>
    axios.post(`${API_URL}/auth/2fa/disable`, { current_password }, { headers: getHeaders() }),
  getLockedAccounts: () => axios.get(`${API_URL}/security/locked-accounts`, { headers: getHeaders() }),
  unlockAccount: (user_id) => axios.post(`${API_URL}/security/unlock/${user_id}`, {}, { headers: getHeaders() }),

  changeMyPassword: (current_password, new_password) =>
    axios.post(`${API_URL}/auth/change-password`, { current_password, new_password }, { headers: getHeaders() }),
  changeMyEmail: (current_password, new_email) =>
    axios.post(`${API_URL}/auth/change-email`, { current_password, new_email }, { headers: getHeaders() }),

  
  // Users
  getUsers: () => axios.get(`${API_URL}/users`, { headers: getHeaders() }),
  getSellers: (params) => axios.get(`${API_URL}/users/sellers`, { headers: getHeaders(), params }),
  createUser: (data) => axios.post(`${API_URL}/auth/register`, data, { headers: getHeaders() }),
  createSeller: (data) => axios.post(`${API_URL}/sellers`, data, { headers: getHeaders() }),
  deleteSeller: (id) => axios.delete(`${API_URL}/sellers/${id}`, { headers: getHeaders() }),
  updateUser: (id, data) => axios.put(`${API_URL}/users/${id}`, data, { headers: getHeaders() }),
  deleteUser: (id) => axios.delete(`${API_URL}/users/${id}`, { headers: getHeaders() }),
  resetUserPassword: (id, data) => axios.post(`${API_URL}/users/${id}/reset-password`, data, { headers: getHeaders() }),
  transferOwnership: (data) => axios.post(`${API_URL}/users/transfer-ownership`, data, { headers: getHeaders() }),
  
  // Order Types
  getOrderTypes: () => axios.get(`${API_URL}/order-types`, { headers: getHeaders() }),
  createOrderType: (data) => axios.post(`${API_URL}/order-types`, data, { headers: getHeaders() }),
  updateOrderType: (id, data) => axios.put(`${API_URL}/order-types/${id}`, data, { headers: getHeaders() }),
  deleteOrderType: (id) => axios.delete(`${API_URL}/order-types/${id}`, { headers: getHeaders() }),
  
  // Orders
  getOrders: (params) => axios.get(`${API_URL}/orders`, { headers: getHeaders(), params }),
  getOrdersPaginated: (params) => axios.get(`${API_URL}/orders-paginated`, { headers: getHeaders(), params }),
  backfillOrderDates: (params) => axios.post(`${API_URL}/orders/backfill-order-dates`, null, { headers: getHeaders(), params }),
  getMissingOrderDateCount: () => axios.get(`${API_URL}/orders/missing-order-date-count`, { headers: getHeaders() }),
  bulkSetOrderDate: (data) => axios.post(`${API_URL}/orders/bulk-set-order-date`, data, { headers: getHeaders() }),
  getOrder: (id) => axios.get(`${API_URL}/orders/${id}`, { headers: getHeaders() }),
  createOrder: (data) => axios.post(`${API_URL}/orders`, data, { headers: getHeaders() }),
  updateOrder: (id, data) => axios.put(`${API_URL}/orders/${id}`, data, { headers: getHeaders() }),
  deleteOrder: (id) => axios.delete(`${API_URL}/orders/${id}`, { headers: getHeaders() }),
  hardDeleteOrder: (id) => axios.delete(`${API_URL}/orders/${id}?hard=true`, { headers: getHeaders() }),
  restoreOrder: (id) => axios.post(`${API_URL}/orders/${id}/restore`, {}, { headers: getHeaders() }),
  toggleDepositPaid: (id) => axios.post(`${API_URL}/orders/${id}/toggle-deposit`, {}, { headers: getHeaders() }),
  toggleOrderPaid: (id) => axios.post(`${API_URL}/orders/${id}/toggle-paid`, {}, { headers: getHeaders() }),
  sendDepositReminder: (id, data) => axios.post(`${API_URL}/orders/${id}/send-deposit-reminder`, data, { headers: getHeaders() }),

  // Clients CRM (aggregated from orders)
  getClients: (params = {}) => axios.get(`${API_URL}/clients`, { headers: getHeaders(), params }),
  createClient: (data) => axios.post(`${API_URL}/clients`, data, { headers: getHeaders() }),
  getClientDetail: (key, params = {}) => axios.get(`${API_URL}/clients/${encodeURIComponent(key)}`, { headers: getHeaders(), params }),
  getClientSmsHistory: (key, name) =>
    axios.get(`${API_URL}/clients/${encodeURIComponent(key)}/sms-history`, { params: { name }, headers: getHeaders() }),
  getPublicRdv: (token) => axios.get(`${API_URL}/public/rdv/${encodeURIComponent(token)}`),
  replyPublicRdv: (token, data) => axios.post(`${API_URL}/public/rdv/${encodeURIComponent(token)}`, data),
  getUnseenSavResponses: () => axios.get(`${API_URL}/sav-client-responses/unseen`, { headers: getHeaders() }),
  markSavResponseSeen: (savId) => axios.post(`${API_URL}/sav-client-responses/${savId}/seen`, {}, { headers: getHeaders() }),
  updateClientSmsPreferences: (key, data) => axios.put(`${API_URL}/clients/${encodeURIComponent(key)}/sms-preferences`, data, { headers: getHeaders() }),
  updateClientProfile: (key, data) => axios.put(`${API_URL}/clients/${encodeURIComponent(key)}/profile`, data, { headers: getHeaders() }),
  deleteClientProfile: (key) => axios.delete(`${API_URL}/clients/${encodeURIComponent(key)}/profile`, { headers: getHeaders() }),
  previewRenameClient: (key, params) => axios.get(`${API_URL}/clients/${encodeURIComponent(key)}/rename-preview`, { headers: getHeaders(), params }),
  renameClient: (key, data) => axios.post(`${API_URL}/clients/${encodeURIComponent(key)}/rename`, data, { headers: getHeaders() }),
  downloadClientsImportTemplate: () => axios.get(`${API_URL}/clients-import/template`, { headers: getHeaders(), responseType: "blob" }),
  importClientsExcel: (formData) => axios.post(`${API_URL}/clients-import/excel`, formData, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } }),
  geocodeAllClients: () => axios.post(`${API_URL}/clients-geocode/all`, {}, { headers: getHeaders() }),
  geocodeClient: (key) => axios.post(`${API_URL}/clients/${encodeURIComponent(key)}/geocode`, {}, { headers: getHeaders() }),
  sendClientDepositReminder: (key, data = {}) => axios.post(`${API_URL}/clients/${encodeURIComponent(key)}/send-deposit-reminder`, data, { headers: getHeaders() }),
  toggleOrderHold: (id, data) => axios.post(`${API_URL}/orders/${id}/toggle-hold`, data, { headers: getHeaders() }),
  
  // Order Payments
  getOrderPayments: (orderId) => axios.get(`${API_URL}/orders/${orderId}/payments`, { headers: getHeaders() }),
  addOrderPayment: (orderId, data) => axios.post(`${API_URL}/orders/${orderId}/payments`, data, { headers: getHeaders() }),
  deleteOrderPayment: (orderId, paymentId) => axios.delete(`${API_URL}/orders/${orderId}/payments/${paymentId}`, { headers: getHeaders() }),

  // Phase 183 — Order invoices (séparées des paiements)
  addOrderInvoice: (orderId, data) => axios.post(`${API_URL}/orders/${orderId}/invoices`, data, { headers: getHeaders() }),
  updateOrderInvoice: (invoiceId, data) => axios.put(`${API_URL}/order-invoices/${invoiceId}`, data, { headers: getHeaders() }),
  deleteOrderInvoice: (invoiceId) => axios.delete(`${API_URL}/order-invoices/${invoiceId}`, { headers: getHeaders() }),
  
  // Order Progress
  getOrderProgress: (params) => axios.get(`${API_URL}/order-progress`, { headers: getHeaders(), params }),
  getAllOrdersWithProgress: (params) => axios.get(`${API_URL}/order-progress/all-orders`, { headers: getHeaders(), params }),
  getOrderProgressCumulatives: () => axios.get(`${API_URL}/order-progress/cumulatives`, { headers: getHeaders() }),
  findOrderDuplicates: (params) => axios.get(`${API_URL}/orders-duplicates`, { headers: getHeaders(), params }),
  findClientDuplicates: () => axios.get(`${API_URL}/clients-duplicates`, { headers: getHeaders() }),
  bulkDeleteClients: (data) => axios.post(`${API_URL}/clients-bulk-delete`, data, { headers: getHeaders() }),
  mergeClients: (data) => axios.post(`${API_URL}/clients-merge`, data, { headers: getHeaders() }),
  getDuplicatesSummary: () => axios.get(`${API_URL}/duplicates-summary`, { headers: getHeaders() }),
  getHealthSummary: () => axios.get(`${API_URL}/health-summary`, { headers: getHeaders() }),
  bulkUpdateOrders: (data) => axios.post(`${API_URL}/orders-bulk-update`, data, { headers: getHeaders() }),
  bulkUpdateClients: (data) => axios.post(`${API_URL}/clients-bulk-update`, data, { headers: getHeaders() }),
  bulkRestoreClients: (data) => axios.post(`${API_URL}/clients-bulk-restore`, data, { headers: getHeaders() }),
  promoteToAdmin: (data) => axios.post(`${API_URL}/admin-tools/promote-to-admin`, data, { headers: getHeaders() }),
  demoteFromAdmin: (data) => axios.post(`${API_URL}/admin-tools/demote-from-admin`, data, { headers: getHeaders() }),
  createOrderProgress: (data) => axios.post(`${API_URL}/order-progress`, data, { headers: getHeaders() }),
  getMonthlyCAFromProgress: (params) => axios.get(`${API_URL}/order-progress/monthly-ca`, { headers: getHeaders(), params }),
  deleteOrderProgress: (id) => axios.delete(`${API_URL}/order-progress/${id}`, { headers: getHeaders() }),
  
  // Commission Rates
  getCommissionRates: (params) => axios.get(`${API_URL}/commission-rates`, { headers: getHeaders(), params }),
  createCommissionRate: (data) => axios.post(`${API_URL}/commission-rates`, data, { headers: getHeaders() }),
  deleteCommissionRate: (id) => axios.delete(`${API_URL}/commission-rates/${id}`, { headers: getHeaders() }),

  // Seller commission criteria
  getSellerCommissionCriteria: (sellerId) => axios.get(`${API_URL}/sellers/${sellerId}/commission-criteria`, { headers: getHeaders() }),
  createSellerCommissionCriterion: (sellerId, data) => axios.post(`${API_URL}/sellers/${sellerId}/commission-criteria`, data, { headers: getHeaders() }),
  updateSellerCommissionCriterion: (sellerId, id, data) => axios.put(`${API_URL}/sellers/${sellerId}/commission-criteria/${id}`, data, { headers: getHeaders() }),
  deleteSellerCommissionCriterion: (sellerId, id) => axios.delete(`${API_URL}/sellers/${sellerId}/commission-criteria/${id}`, { headers: getHeaders() }),
  copySellerCommissionCriteria: (sellerId, srcSellerId) => axios.post(`${API_URL}/sellers/${sellerId}/commission-criteria/copy-from/${srcSellerId}`, {}, { headers: getHeaders() }),

  // Order commission validation
  getOrderCommissionValidation: (orderId) => axios.get(`${API_URL}/commissions/order-validation/${orderId}`, { headers: getHeaders() }),
  saveOrderCommissionValidation: (orderId, data) => axios.put(`${API_URL}/commissions/order-validation/${orderId}`, data, { headers: getHeaders() }),
  
  // Monthly Targets
  getMonthlyTargets: (params) => axios.get(`${API_URL}/monthly-targets`, { headers: getHeaders(), params }),
  createMonthlyTarget: (data) => axios.post(`${API_URL}/monthly-targets`, data, { headers: getHeaders() }),
  
  // Dashboard
  getDashboardStats: (params) => axios.get(`${API_URL}/dashboard/stats`, { headers: getHeaders(), params }),
  getDashboardPendingValidations: () => axios.get(`${API_URL}/dashboard/pending-validations`, { headers: getHeaders() }),
  getOutstandingReceivables: () => axios.get(`${API_URL}/dashboard/outstanding-receivables`, { headers: getHeaders() }),
  getSellerMonthlyProgress: (params) => axios.get(`${API_URL}/dashboard/seller-monthly-progress`, { headers: getHeaders(), params }),
  getSellerAlertSettings: () => axios.get(`${API_URL}/dashboard/seller-alert-settings`, { headers: getHeaders() }),
  updateSellerAlertSettings: (data) => axios.put(`${API_URL}/dashboard/seller-alert-settings`, data, { headers: getHeaders() }),
  runSellerAlertsNow: () => axios.post(`${API_URL}/dashboard/seller-alerts/run-now`, {}, { headers: getHeaders() }),
  updateSeller: (id, data) => axios.put(`${API_URL}/sellers/${id}`, data, { headers: getHeaders() }),
  sendPaymentReminder: (data) => axios.post(`${API_URL}/dashboard/send-reminder`, data, { headers: getHeaders() }),
  getPlanning: () => axios.get(`${API_URL}/dashboard/planning`, { headers: getHeaders() }),

  // App config (per-org settings)
  getAppConfig: () => axios.get(`${API_URL}/app-config`, { headers: getHeaders() }),
  updateAppConfig: (data) => axios.put(`${API_URL}/app-config`, data, { headers: getHeaders() }),
  
  // Commissions
  getSellerCommissions: (params) => axios.get(`${API_URL}/commissions/sellers`, { headers: getHeaders(), params }),
  getArchivedSellersCommissions: () => axios.get(`${API_URL}/commissions/archived-sellers`, { headers: getHeaders() }),
  getUpcomingCommissions: () => axios.get(`${API_URL}/commissions/upcoming`, { headers: getHeaders() }),
  getManagerCommission: (params) => axios.get(`${API_URL}/commissions/manager`, { headers: getHeaders(), params }),
  
  // Import
  importExcel: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post(`${API_URL}/import/excel`, formData, {
      headers: { ...getHeaders(), "Content-Type": "multipart/form-data" }
    });
  },
  getImportedData: () => axios.get(`${API_URL}/import/data`, { headers: getHeaders() }),
  deleteAllImports: () => axios.delete(`${API_URL}/import/all`, { headers: getHeaders() }),
  createManualCA: (data) => axios.post(`${API_URL}/import/manual`, data, { headers: getHeaders() }),
  
  // Export
  exportOrders: (params) => axios.get(`${API_URL}/export/orders`, { headers: getHeaders(), params }),
  exportCommissions: (params) => axios.get(`${API_URL}/export/commissions`, { headers: getHeaders(), params }),
  
  // Reports
  getReportBySeller: (params) => axios.get(`${API_URL}/reports/by-seller`, { headers: getHeaders(), params }),
  
  // Stock Forecast
  getStockForecast: () => axios.get(`${API_URL}/stock/forecast`, { headers: getHeaders() }),
  
  // Non-Working Days
  getNonWorkingDays: (params) => axios.get(`${API_URL}/non-working-days`, { headers: getHeaders(), params }),
  getCalendarColors: () => axios.get(`${API_URL}/calendar-settings/colors`, { headers: getHeaders() }),
  updateCalendarColors: (data) => axios.put(`${API_URL}/calendar-settings/colors`, data, { headers: getHeaders() }),
  createNonWorkingDay: (data) => axios.post(`${API_URL}/non-working-days`, data, { headers: getHeaders() }),
  deleteNonWorkingDay: (id) => axios.delete(`${API_URL}/non-working-days/${id}`, { headers: getHeaders() }),
  restoreHoliday: (dateStr) => axios.post(`${API_URL}/non-working-days/restore/${dateStr}`, {}, { headers: getHeaders() }),
  
  // Planning Calendar
  getPlanningCalendar: (params) => axios.get(`${API_URL}/planning/calendar`, { headers: getHeaders(), params }),
  reorderPlanning: (orderIds) => axios.put(`${API_URL}/planning/reorder`, orderIds, { headers: getHeaders() }),
  updateOrderPlanningConstraints: (orderId, data) => axios.put(`${API_URL}/orders/${orderId}/planning-constraints`, null, { headers: getHeaders(), params: data }),
  
  // Analytics
  getStockEvolution: (params) => axios.get(`${API_URL}/analytics/stock-evolution`, { headers: getHeaders(), params }),
  getCaEvolution: (params) => axios.get(`${API_URL}/analytics/ca-evolution`, { headers: getHeaders(), params }),
  getOrdersEvolution: (params) => axios.get(`${API_URL}/analytics/orders-evolution`, { headers: getHeaders(), params }),
  getOrdersBreakdown: (params) => axios.get(`${API_URL}/analytics/orders-breakdown`, { headers: getHeaders(), params }),
  getCaBreakdown: (params) => axios.get(`${API_URL}/analytics/ca-breakdown`, { headers: getHeaders(), params }),

  // Analytics V2 (refonte Phase 159)
  getAnalyticsPeriods: (params) => axios.get(`${API_URL}/analytics-v2/periods`, { headers: getHeaders(), params }),
  getAnalyticsStockInstant: () => axios.get(`${API_URL}/analytics-v2/stock-instant`, { headers: getHeaders() }),
  // === SUBCONTRACTOR COMPLIANCE (Phase 175) ===
  // Doc types library (org-wide)
  getSubcontractorDocTypes: () => axios.get(`${API_URL}/subcontractor-doc-types`, { headers: getHeaders() }),
  createSubcontractorDocType: (data) => axios.post(`${API_URL}/subcontractor-doc-types`, data, { headers: getHeaders() }),
  updateSubcontractorDocType: (id, data) => axios.patch(`${API_URL}/subcontractor-doc-types/${id}`, data, { headers: getHeaders() }),
  deleteSubcontractorDocType: (id) => axios.delete(`${API_URL}/subcontractor-doc-types/${id}`, { headers: getHeaders() }),
  // Per-subcontractor documents
  getSubcontractorDocuments: (subId) => axios.get(`${API_URL}/subcontractors/${subId}/documents`, { headers: getHeaders() }),
  uploadSubcontractorDocument: (subId, data) => axios.post(`${API_URL}/subcontractors/${subId}/documents`, data, { headers: getHeaders() }),
  getSubcontractorDocumentFile: (subId, docId) => axios.get(`${API_URL}/subcontractors/${subId}/documents/${docId}/file`, { headers: getHeaders() }),
  deleteSubcontractorDocument: (subId, docId) => axios.delete(`${API_URL}/subcontractors/${subId}/documents/${docId}`, { headers: getHeaders() }),
  // Compliance summary + dashboard alerts
  getSubcontractorsComplianceSummary: () => axios.get(`${API_URL}/subcontractors-compliance-summary`, { headers: getHeaders() }),
  getDashboardSubcontractorAlerts: () => axios.get(`${API_URL}/dashboard/subcontractor-compliance-alerts`, { headers: getHeaders() }),
  exportSubcontractorCompliancePdf: (subId) => axios.get(`${API_URL}/subcontractors/${subId}/compliance-pdf`, { headers: getHeaders() }),

  getAnalyticsMultiYear: (ranges, params) => axios.post(`${API_URL}/analytics-v2/multi-year`, ranges, { headers: getHeaders(), params }),
  postAnalyticsAdvancedCompare: (payload) => axios.post(`${API_URL}/analytics-v2/advanced-compare`, payload, { headers: getHeaders() }),
  getAnalyticsByOrderType: (params) => axios.get(`${API_URL}/analytics-v2/by-order-type`, { headers: getHeaders(), params }),
  getAnalyticsSellersList: () => axios.get(`${API_URL}/analytics-v2/sellers-list`, { headers: getHeaders() }),
  getAnalyticsCaDetail: (year, month, params) => axios.get(`${API_URL}/analytics-v2/ca-detail/${year}/${month}`, { headers: getHeaders(), params }),
  getAnalyticsOrdersDetail: (year, month, params) => axios.get(`${API_URL}/analytics-v2/orders-detail/${year}/${month}`, { headers: getHeaders(), params }),
  listHistoricalImports: () => axios.get(`${API_URL}/analytics-v2/historical-imports`, { headers: getHeaders() }),
  upsertHistoricalImport: (entry) => axios.post(`${API_URL}/analytics-v2/historical-imports`, entry, { headers: getHeaders() }),
  updateHistoricalImport: (id, entry) => axios.put(`${API_URL}/analytics-v2/historical-imports/${id}`, entry, { headers: getHeaders() }),
  deleteHistoricalImportEntry: (id) => axios.delete(`${API_URL}/analytics-v2/historical-imports/${id}`, { headers: getHeaders() }),
  deleteHistoricalImportBatch: (importId) => axios.delete(`${API_URL}/analytics-v2/historical-imports/by-import/${importId}`, { headers: getHeaders() }),
  uploadHistoricalImportExcel: (file, label) => {
    const form = new FormData();
    form.append("file", file);
    if (label) form.append("label", label);
    return axios.post(`${API_URL}/analytics-v2/historical-imports/upload-excel`, form, {
      headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      params: label ? { label } : undefined,
    });
  },
  downloadHistoricalImportTemplate: () => axios.get(`${API_URL}/analytics-v2/historical-imports/template`, { headers: getHeaders(), responseType: "blob" }),
  getObjectivesProgress: (params) => axios.get(`${API_URL}/analytics/objectives-progress`, { headers: getHeaders(), params }),
  
  // Annual Objectives
  getAnnualObjectives: (params) => axios.get(`${API_URL}/annual-objectives`, { headers: getHeaders(), params }),
  createAnnualObjective: (data) => axios.post(`${API_URL}/annual-objectives`, data, { headers: getHeaders() }),
  deleteAnnualObjective: (id) => axios.delete(`${API_URL}/annual-objectives/${id}`, { headers: getHeaders() }),
  cleanupOrphanObjectives: () => axios.post(`${API_URL}/annual-objectives/cleanup-orphans`, {}, { headers: getHeaders() }),
  
  // Seller Bonuses
  getSellerBonuses: (params) => axios.get(`${API_URL}/seller-bonuses`, { headers: getHeaders(), params }),
  createSellerBonus: (data) => axios.post(`${API_URL}/seller-bonuses`, data, { headers: getHeaders() }),
  deleteSellerBonus: (id) => axios.delete(`${API_URL}/seller-bonuses/${id}`, { headers: getHeaders() }),

  // Employees (Salariés)
  getEmployees: () => axios.get(`${API_URL}/employees`, { headers: getHeaders() }),
  getEmployee: (id) => axios.get(`${API_URL}/employees/${id}`, { headers: getHeaders() }),
  createEmployee: (data) => axios.post(`${API_URL}/employees`, data, { headers: getHeaders() }),
  updateEmployee: (id, data) => axios.put(`${API_URL}/employees/${id}`, data, { headers: getHeaders() }),
  deleteEmployee: (id) => axios.delete(`${API_URL}/employees/${id}`, { headers: getHeaders() }),
  getEmployeeStats: (id, params) => axios.get(`${API_URL}/employees/${id}/stats`, { headers: getHeaders(), params }),
  // Absences
  getAbsences: (employeeId, params) => axios.get(`${API_URL}/employees/${employeeId}/absences`, { headers: getHeaders(), params }),
  addAbsence: (employeeId, data) => axios.post(`${API_URL}/employees/${employeeId}/absences`, data, { headers: getHeaders() }),
  deleteAbsence: (absenceId) => axios.delete(`${API_URL}/absences/${absenceId}`, { headers: getHeaders() }),
  getAbsencesCalendar: (params) => axios.get(`${API_URL}/absences/calendar`, { headers: getHeaders(), params }),
  getWorkEntriesCalendar: (params) => axios.get(`${API_URL}/work-entries/calendar`, { headers: getHeaders(), params }),
  // Bonuses (primes)
  getEmployeeBonuses: (employeeId) => axios.get(`${API_URL}/employees/${employeeId}/bonuses`, { headers: getHeaders() }),
  addEmployeeBonus: (employeeId, data) => axios.post(`${API_URL}/employees/${employeeId}/bonuses`, data, { headers: getHeaders() }),
  deleteEmployeeBonus: (bonusId) => axios.delete(`${API_URL}/bonuses/${bonusId}`, { headers: getHeaders() }),
  // PVs
  getEmployeePVs: (employeeId) => axios.get(`${API_URL}/employees/${employeeId}/pvs`, { headers: getHeaders() }),
  addEmployeePV: (employeeId, data) => axios.post(`${API_URL}/employees/${employeeId}/pvs`, data, { headers: getHeaders() }),
  deleteEmployeePV: (pvId) => axios.delete(`${API_URL}/pvs/${pvId}`, { headers: getHeaders() }),

  // Subcontractors (Sous-traitants)
  getSubcontractors: (params) => axios.get(`${API_URL}/subcontractors`, { headers: getHeaders(), params }),
  getSubcontractor: (id) => axios.get(`${API_URL}/subcontractors/${id}`, { headers: getHeaders() }),
  createSubcontractor: (data) => axios.post(`${API_URL}/subcontractors`, data, { headers: getHeaders() }),
  updateSubcontractor: (id, data) => axios.put(`${API_URL}/subcontractors/${id}`, data, { headers: getHeaders() }),
  deleteSubcontractor: (id) => axios.delete(`${API_URL}/subcontractors/${id}`, { headers: getHeaders() }),
  getSubcontractorOrders: (id) => axios.get(`${API_URL}/subcontractors/${id}/orders`, { headers: getHeaders() }),

  // Workforce
  workforceCheck: (params) => axios.get(`${API_URL}/workforce/check`, { headers: getHeaders(), params }),

  // Admin: Users performance dashboard
  getUsersPerformance: (params) => axios.get(`${API_URL}/admin/users-performance`, { headers: getHeaders(), params }),

  // Employee trainings
  getTrainings: (empId) => axios.get(`${API_URL}/employees/${empId}/trainings`, { headers: getHeaders() }),
  addTraining: (empId, data) => axios.post(`${API_URL}/employees/${empId}/trainings`, data, { headers: getHeaders() }),
  updateTraining: (id, data) => axios.put(`${API_URL}/trainings/${id}`, data, { headers: getHeaders() }),
  renewTraining: (id, data) => axios.post(`${API_URL}/trainings/${id}/renew`, data, { headers: getHeaders() }),
  deleteTraining: (id) => axios.delete(`${API_URL}/trainings/${id}`, { headers: getHeaders() }),

  // Phase 187 — Maintenance reasons & cross-links
  getMaintenanceReasons: () => axios.get(`${API_URL}/maintenance-reasons`, { headers: getHeaders() }),
  createMaintenanceReason: (data) => axios.post(`${API_URL}/maintenance-reasons`, data, { headers: getHeaders() }),
  updateMaintenanceReason: (id, data) => axios.put(`${API_URL}/maintenance-reasons/${id}`, data, { headers: getHeaders() }),
  deleteMaintenanceReason: (id) => axios.delete(`${API_URL}/maintenance-reasons/${id}`, { headers: getHeaders() }),
  updateVehicleMaintenance: (id, data) => axios.put(`${API_URL}/maintenance/${id}`, data, { headers: getHeaders() }),
  getEquipmentLinkedSuppliers: (equipmentId) => axios.get(`${API_URL}/equipment/${equipmentId}/linked-suppliers`, { headers: getHeaders() }),
  getSupplierLinkedEquipment: (supplierId) => axios.get(`${API_URL}/suppliers/${supplierId}/linked-equipment`, { headers: getHeaders() }),
  getTrainingAlerts: () => axios.get(`${API_URL}/trainings/alerts`, { headers: getHeaders() }),
  getTrainingsListing: () => axios.get(`${API_URL}/trainings/listing`, { headers: getHeaders() }),

  // Late orders
  getLateOrders: () => axios.get(`${API_URL}/late-orders`, { headers: getHeaders() }),

  // Notifications
  getNotificationSettings: () => axios.get(`${API_URL}/notifications/settings`, { headers: getHeaders() }),
  updateNotificationSettings: (data) => axios.put(`${API_URL}/notifications/settings`, data, { headers: getHeaders() }),
  sendTestEmail: () => axios.post(`${API_URL}/notifications/send-test`, {}, { headers: getHeaders() }),
  sendLateOrdersNotification: () => axios.post(`${API_URL}/notifications/send-late-orders`, {}, { headers: getHeaders() }),
  sendWeeklySummary: () => axios.post(`${API_URL}/notifications/send-weekly-summary`, {}, { headers: getHeaders() }),

  // Scheduler
  getSchedulerStatus: () => axios.get(`${API_URL}/scheduler/status`, { headers: getHeaders() }),
  triggerSchedulerNow: () => axios.post(`${API_URL}/scheduler/run-now`, {}, { headers: getHeaders() }),

  // Backup
  getBackupSettings: () => axios.get(`${API_URL}/backup/settings`, { headers: getHeaders() }),
  updateBackupSettings: (data) => axios.put(`${API_URL}/backup/settings`, data, { headers: getHeaders() }),
  getBackupStatus: () => axios.get(`${API_URL}/backup/status`, { headers: getHeaders() }),
  downloadBackup: () => axios.get(`${API_URL}/backup/download`, { headers: getHeaders(), responseType: "blob" }),
  sendBackupEmail: (email) => axios.post(`${API_URL}/backup/send-email`, { email: email || null }, { headers: getHeaders() }),
  previewRestore: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API_URL}/backup/restore/preview`, fd, {
      headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
    });
  },
  restoreBackup: (file, mode) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", mode || "merge");
    return axios.post(`${API_URL}/backup/restore`, fd, {
      headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
  },

  // Equipment (Matériel)
  getEquipment: (params) => axios.get(`${API_URL}/equipment`, { headers: getHeaders(), params }),
  getEquipmentItem: (id) => axios.get(`${API_URL}/equipment/${id}`, { headers: getHeaders() }),
  createEquipment: (data) => axios.post(`${API_URL}/equipment`, data, { headers: getHeaders() }),
  updateEquipment: (id, data) => axios.put(`${API_URL}/equipment/${id}`, data, { headers: getHeaders() }),
  deleteEquipment: (id) => axios.delete(`${API_URL}/equipment/${id}`, { headers: getHeaders() }),
  getEquipmentAlerts: () => axios.get(`${API_URL}/equipment/alerts/expiring`, { headers: getHeaders() }),
  getInventorySummary: () => axios.get(`${API_URL}/equipment/inventory/summary`, { headers: getHeaders() }),
  getInventoryDashboard: () => axios.get(`${API_URL}/equipment/inventory/dashboard`, { headers: getHeaders() }),
  // Vehicle inspections
  getVehicleInspections: (id) => axios.get(`${API_URL}/equipment/${id}/inspections`, { headers: getHeaders() }),
  addVehicleInspection: (id, data) => axios.post(`${API_URL}/equipment/${id}/inspections`, data, { headers: getHeaders() }),
  deleteVehicleInspection: (id) => axios.delete(`${API_URL}/inspections/${id}`, { headers: getHeaders() }),
  // Vehicle maintenance
  getVehicleMaintenance: (id) => axios.get(`${API_URL}/equipment/${id}/maintenance`, { headers: getHeaders() }),
  addVehicleMaintenance: (id, data) => axios.post(`${API_URL}/equipment/${id}/maintenance`, data, { headers: getHeaders() }),
  deleteVehicleMaintenance: (id) => axios.delete(`${API_URL}/maintenance/${id}`, { headers: getHeaders() }),

  // SAV
  getSAVs: (params) => axios.get(`${API_URL}/sav`, { headers: getHeaders(), params }),
  getSAV: (id) => axios.get(`${API_URL}/sav/${id}`, { headers: getHeaders() }),
  createSAV: (data) => axios.post(`${API_URL}/sav`, data, { headers: getHeaders() }),
  updateSAV: (id, data) => axios.put(`${API_URL}/sav/${id}`, data, { headers: getHeaders() }),
  deleteSAV: (id) => axios.delete(`${API_URL}/sav/${id}`, { headers: getHeaders() }),
  getSAVStatsByEmployee: () => axios.get(`${API_URL}/sav/stats/by-employee`, { headers: getHeaders() }),
  getSAVsForEmployee: (empId) => axios.get(`${API_URL}/sav/employee/${empId}`, { headers: getHeaders() }),
  getSAVsForSubcontractor: (subId) => axios.get(`${API_URL}/sav/subcontractor/${subId}`, { headers: getHeaders() }),
  getSAVCalendar: (params) => axios.get(`${API_URL}/sav/calendar`, { headers: getHeaders(), params }),

  // SAV attachments + PDF
  listSAVAttachments: (savId) => axios.get(`${API_URL}/sav/${savId}/attachments`, { headers: getHeaders() }),
  uploadSAVAttachment: (savId, file) => {
    const form = new FormData();
    form.append("file", file);
    return axios.post(`${API_URL}/sav/${savId}/attachments`, form, {
      headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
    });
  },
  deleteSAVAttachment: (savId, attId) => axios.delete(`${API_URL}/sav/${savId}/attachments/${attId}`, { headers: getHeaders() }),
  getSAVWorksheetPdf: (savId, includeAttachments) => axios.get(
    `${API_URL}/sav/${savId}/worksheet-pdf`,
    { headers: getHeaders(), params: { include_attachments: includeAttachments }, responseType: "blob" },
  ),

  // Work tracking
  getWorkSlots: (date) => axios.get(`${API_URL}/work-tracking/slots`, { headers: getHeaders(), params: { date_str: date } }),
  getWorkEntries: (params) => axios.get(`${API_URL}/work-tracking`, { headers: getHeaders(), params }),
  createWorkEntry: (data) => axios.post(`${API_URL}/work-tracking`, data, { headers: getHeaders() }),
  updateWorkEntry: (id, data) => axios.patch(`${API_URL}/work-tracking/${id}`, data, { headers: getHeaders() }),
  createWorkEntriesBatch: (data) => axios.post(`${API_URL}/work-tracking/batch`, data, { headers: getHeaders() }),
  deleteWorkEntry: (id) => axios.delete(`${API_URL}/work-tracking/${id}`, { headers: getHeaders() }),
  clearWorkEntries: (params) => axios.delete(`${API_URL}/work-tracking/clear`, { headers: getHeaders(), params }),
  getWorkComparison: (params) => axios.get(`${API_URL}/work-tracking/comparison`, { headers: getHeaders(), params }),
  getOrdersTimeDeltas: () => axios.get(`${API_URL}/work-tracking/orders-time-deltas`, { headers: getHeaders() }),
  getOrdersProfitability: () => axios.get(`${API_URL}/work-tracking/orders-profitability`, { headers: getHeaders() }),
  getEmployeeHoursSummary: (employeeId, params) => axios.get(`${API_URL}/employees/${employeeId}/hours-summary`, { headers: getHeaders(), params }),
  getHoursDefaults: () => axios.get(`${API_URL}/hours/defaults`, { headers: getHeaders() }),
  getOvertimeConfig: () => axios.get(`${API_URL}/hr/overtime-config`, { headers: getHeaders() }),
  updateOvertimeConfig: (data) => axios.patch(`${API_URL}/hr/overtime-config`, data, { headers: getHeaders() }),
  getTopOrders: (params) => axios.get(`${API_URL}/dashboard/top-orders`, { headers: getHeaders(), params }),

  // AI Assistant
  aiInterpret: (text) => axios.post(`${API_URL}/ai/interpret`, { text }, { headers: getHeaders() }),
  aiExecute: (actionId) => axios.post(`${API_URL}/ai/execute`, { action_id: actionId }, { headers: getHeaders() }),
  aiCancel: (actionId) => axios.delete(`${API_URL}/ai/actions/${actionId}`, { headers: getHeaders() }),
  aiHistory: (limit = 50) => axios.get(`${API_URL}/ai/history`, { headers: getHeaders(), params: { limit } }),
  aiStats: (days = 30) => axios.get(`${API_URL}/ai/stats`, { headers: getHeaders(), params: { days } }),
  aiHistoryExportUrl: () => `${API_URL}/ai/history/export`,
  aiActionDetail: (actionId) => axios.get(`${API_URL}/ai/actions/${actionId}`, { headers: getHeaders() }),
  aiSuggestions: (limit = 5) => axios.get(`${API_URL}/ai/suggestions`, { headers: getHeaders(), params: { limit } }),
  // Macros
  aiListMacros: () => axios.get(`${API_URL}/ai/macros`, { headers: getHeaders() }),
  aiCreateMacro: (data) => axios.post(`${API_URL}/ai/macros`, data, { headers: getHeaders() }),
  aiUpdateMacro: (id, data) => axios.put(`${API_URL}/ai/macros/${id}`, data, { headers: getHeaders() }),
  aiDeleteMacro: (id) => axios.delete(`${API_URL}/ai/macros/${id}`, { headers: getHeaders() }),
  aiTranscribe: (audioBlob, filename = "audio.webm") => {
    const fd = new FormData();
    fd.append("file", audioBlob, filename);
    return axios.post(`${API_URL}/ai/transcribe`, fd, {
      headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
    });
  },
  getOverrunSettings: () => axios.get(`${API_URL}/overrun-alerts/settings`, { headers: getHeaders() }),
  updateOverrunSettings: (data) => axios.put(`${API_URL}/overrun-alerts/settings`, data, { headers: getHeaders() }),
  getWorkerProfitability: (params) => axios.get(`${API_URL}/analytics/worker-profitability`, { headers: getHeaders(), params }),

  // Email templates (PV completion, deposit reminder, balance reminder)
  listEmailTemplates: () => axios.get(`${API_URL}/email-templates`, { headers: getHeaders() }),
  getEmailTemplate: (type) => axios.get(`${API_URL}/email-templates/${type}`, { headers: getHeaders() }),
  updateEmailTemplate: (type, data) => axios.put(`${API_URL}/email-templates/${type}`, data, { headers: getHeaders() }),
  testSendEmailTemplate: (type, data) => axios.post(`${API_URL}/email-templates/${type}/test-send`, data, { headers: getHeaders() }),
  getEmailTemplateVariables: () => axios.get(`${API_URL}/email-templates/variables`, { headers: getHeaders() }),
  sendBalanceReminder: (orderId, data) => axios.post(`${API_URL}/orders/${orderId}/send-balance-reminder`, data, { headers: getHeaders() }),
  resendClientEmail: (emailId, data) => axios.post(`${API_URL}/email-templates/client-emails/${emailId}/resend`, data || {}, { headers: getHeaders() }),

  // Historical orders import (Phase 86)
  previewOrdersImport: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API_URL}/import/orders/preview`, fd, {
      headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
    });
  },
  confirmOrdersImport: (data) => axios.post(`${API_URL}/import/orders/confirm`, data, { headers: getHeaders() }),
  rollbackOrdersImport: (batchId) => axios.post(`${API_URL}/import/orders/${batchId}/rollback`, {}, { headers: getHeaders() }),
  listOrdersImportBatches: () => axios.get(`${API_URL}/import/orders/batches`, { headers: getHeaders() }),
  ordersImportTemplateUrl: () => `${API_URL}/import/orders/template`,

  // Travel bonuses (Primes de petit déplacement)
  getTravelBonusSettings: () => axios.get(`${API_URL}/travel-bonuses/settings`, { headers: getHeaders() }),
  updateTravelBonusSettings: (data) => axios.put(`${API_URL}/travel-bonuses/settings`, data, { headers: getHeaders() }),
  getTravelBonusRates: (params) => axios.get(`${API_URL}/travel-bonuses/rates`, { headers: getHeaders(), params }),
  createTravelBonusRate: (data) => axios.post(`${API_URL}/travel-bonuses/rates`, data, { headers: getHeaders() }),
  updateTravelBonusRate: (id, data) => axios.put(`${API_URL}/travel-bonuses/rates/${id}`, data, { headers: getHeaders() }),
  deleteTravelBonusRate: (id) => axios.delete(`${API_URL}/travel-bonuses/rates/${id}`, { headers: getHeaders() }),
  computeTravelBonuses: (params) => axios.get(`${API_URL}/travel-bonuses/compute`, { headers: getHeaders(), params }),

  // Push notifications
  getPushVapidKey: () => axios.get(`${API_URL}/push/vapid-public-key`, { headers: getHeaders() }),
  subscribePush: (data) => axios.post(`${API_URL}/push/subscribe`, data, { headers: getHeaders() }),
  unsubscribePush: (data) => axios.post(`${API_URL}/push/unsubscribe`, data, { headers: getHeaders() }),
  getPushStatus: () => axios.get(`${API_URL}/push/status`, { headers: getHeaders() }),
  updatePushPreferences: (data) => axios.put(`${API_URL}/push/preferences`, data, { headers: getHeaders() }),
  sendPushTest: (data) => axios.post(`${API_URL}/push/test`, data, { headers: getHeaders() }),

  // Google Calendar integration
  getGoogleCalendarStatus: () => axios.get(`${API_URL}/google-calendar/status`, { headers: getHeaders() }),
  getGoogleCalendarAuthUrl: () => axios.get(`${API_URL}/google-calendar/connect`, { headers: getHeaders() }),
  disconnectGoogleCalendar: (removeCalendars = false) => axios.post(`${API_URL}/google-calendar/disconnect?remove_calendars=${removeCalendars}`, {}, { headers: getHeaders() }),
  updateGoogleCalendarPreferences: (data) => axios.put(`${API_URL}/google-calendar/preferences`, data, { headers: getHeaders() }),
  syncAllGoogleCalendar: () => axios.post(`${API_URL}/google-calendar/sync-all`, {}, { headers: getHeaders() }),

  // Order materials (locked products on orders)
  getOrderMaterials: (orderId) => axios.get(`${API_URL}/orders/${orderId}/materials`, { headers: getHeaders() }),
  lockOrderMaterial: (orderId, data) => axios.post(`${API_URL}/orders/${orderId}/materials`, data, { headers: getHeaders() }),
  updateOrderMaterial: (materialId, data) => axios.put(`${API_URL}/order-materials/${materialId}`, data, { headers: getHeaders() }),
  deleteOrderMaterial: (materialId) => axios.delete(`${API_URL}/order-materials/${materialId}`, { headers: getHeaders() }),
  recordOrderMaterialActualUsage: (materialId, data) => axios.post(`${API_URL}/order-materials/${materialId}/actual-usage`, data, { headers: getHeaders() }),
  restoreOrderMaterialToForecast: (materialId) => axios.post(`${API_URL}/order-materials/${materialId}/restore-forecast`, {}, { headers: getHeaders() }),
  getMaterialMargins: () => axios.get(`${API_URL}/analytics/material-margins`, { headers: getHeaders() }),

  // Product stock
  getProductStock: (productId) => axios.get(`${API_URL}/supplier-products/${productId}/stock`, { headers: getHeaders() }),
  addStockEntry: (productId, data) => axios.post(`${API_URL}/supplier-products/${productId}/stock`, data, { headers: getHeaders() }),
  deleteStockEntry: (entryId) => axios.delete(`${API_URL}/product-stock-entries/${entryId}`, { headers: getHeaders() }),
  getSupplierStockForecast: () => axios.get(`${API_URL}/analytics/stock-forecast`, { headers: getHeaders() }),
  getForecastActualDivergence: (params = {}) =>
    axios.get(`${API_URL}/analytics/forecast-actual-divergence`, { headers: getHeaders(), params }),

  // Phase 181 — material set templates (reusable bundles)
  listMaterialSetTemplates: () =>
    axios.get(`${API_URL}/material-set-templates`, { headers: getHeaders() }),
  createMaterialSetTemplate: (data) =>
    axios.post(`${API_URL}/material-set-templates`, data, { headers: getHeaders() }),
  updateMaterialSetTemplate: (tplId, data) =>
    axios.put(`${API_URL}/material-set-templates/${tplId}`, data, { headers: getHeaders() }),
  deleteMaterialSetTemplate: (tplId) =>
    axios.delete(`${API_URL}/material-set-templates/${tplId}`, { headers: getHeaders() }),
  applyMaterialSetTemplate: (orderId, tplId) =>
    axios.post(`${API_URL}/orders/${orderId}/materials/apply-template/${tplId}`, {}, { headers: getHeaders() }),

  // Admin tasks (recurring + backlog)
  getRecurringTasks: () => axios.get(`${API_URL}/admin-tasks/recurring`, { headers: getHeaders() }),
  createRecurringTask: (data) => axios.post(`${API_URL}/admin-tasks/recurring`, data, { headers: getHeaders() }),
  updateRecurringTask: (id, data) => axios.put(`${API_URL}/admin-tasks/recurring/${id}`, data, { headers: getHeaders() }),
  deleteRecurringTask: (id) => axios.delete(`${API_URL}/admin-tasks/recurring/${id}`, { headers: getHeaders() }),
  seedFrFiscalTemplates: () => axios.post(`${API_URL}/admin-tasks/recurring/seed-fr-templates`, {}, { headers: getHeaders() }),
  getTaskOccurrences: (params = {}) => axios.get(`${API_URL}/admin-tasks/recurring/occurrences`, { headers: getHeaders(), params }),
  toggleOccurrence: (data) => axios.post(`${API_URL}/admin-tasks/recurring/occurrences/toggle`, data, { headers: getHeaders() }),
  getBacklogTasks: (params = {}) => axios.get(`${API_URL}/admin-tasks/backlog`, { headers: getHeaders(), params }),
  createBacklogTask: (data) => axios.post(`${API_URL}/admin-tasks/backlog`, data, { headers: getHeaders() }),
  updateBacklogTask: (id, data) => axios.put(`${API_URL}/admin-tasks/backlog/${id}`, data, { headers: getHeaders() }),
  deleteBacklogTask: (id) => axios.delete(`${API_URL}/admin-tasks/backlog/${id}`, { headers: getHeaders() }),
  // Work sessions on an order
  createOrderWorkSessions: (orderId, data) => axios.post(`${API_URL}/orders/${orderId}/work-sessions`, data, { headers: getHeaders() }),
  getOrderWorkers: (orderId) => axios.get(`${API_URL}/orders/${orderId}/workers`, { headers: getHeaders() }),
  getEmployeeOrders: (employeeId) => axios.get(`${API_URL}/employees/${employeeId}/orders`, { headers: getHeaders() }),
  getContracts: () => axios.get(`${API_URL}/contracts`, { headers: getHeaders() }),
  createContract: (data) => axios.post(`${API_URL}/contracts`, data, { headers: getHeaders() }),
  updateContract: (id, data) => axios.put(`${API_URL}/contracts/${id}`, data, { headers: getHeaders() }),
  deleteContract: (id) => axios.delete(`${API_URL}/contracts/${id}`, { headers: getHeaders() }),
  getContractsForecast: (params) => axios.get(`${API_URL}/contracts/forecast`, { headers: getHeaders(), params }),
  migrateExistingContracts: () => axios.post(`${API_URL}/contracts/migrate-existing`, {}, { headers: getHeaders() }),
  getContractCategories: () => axios.get(`${API_URL}/contracts/categories`, { headers: getHeaders() }),
  createContractCategory: (data) => axios.post(`${API_URL}/contracts/categories`, data, { headers: getHeaders() }),
  updateContractCategory: (id, data) => axios.put(`${API_URL}/contracts/categories/${id}`, data, { headers: getHeaders() }),
  deleteContractCategory: (id) => axios.delete(`${API_URL}/contracts/categories/${id}`, { headers: getHeaders() }),
  getAdminTasksDashboardSummary: () => axios.get(`${API_URL}/admin-tasks/dashboard-summary`, { headers: getHeaders() }),

  // Suppliers
  getSuppliers: (params) => axios.get(`${API_URL}/suppliers`, { headers: getHeaders(), params }),
  getSupplier: (id) => axios.get(`${API_URL}/suppliers/${id}`, { headers: getHeaders() }),
  createSupplier: (data) => axios.post(`${API_URL}/suppliers`, data, { headers: getHeaders() }),
  updateSupplier: (id, data) => axios.put(`${API_URL}/suppliers/${id}`, data, { headers: getHeaders() }),
  deleteSupplier: (id) => axios.delete(`${API_URL}/suppliers/${id}`, { headers: getHeaders() }),
  getSupplierUnits: () => axios.get(`${API_URL}/suppliers/units`, { headers: getHeaders() }),

  // Supplier categories
  getSupplierCategories: () => axios.get(`${API_URL}/supplier-categories`, { headers: getHeaders() }),
  createSupplierCategory: (data) => axios.post(`${API_URL}/supplier-categories`, data, { headers: getHeaders() }),
  updateSupplierCategory: (id, data) => axios.put(`${API_URL}/supplier-categories/${id}`, data, { headers: getHeaders() }),
  deleteSupplierCategory: (id) => axios.delete(`${API_URL}/supplier-categories/${id}`, { headers: getHeaders() }),
  // Supplier products
  getSupplierProducts: (supplierId) => axios.get(`${API_URL}/suppliers/${supplierId}/products`, { headers: getHeaders() }),
  getAllProducts: (params = {}) => axios.get(`${API_URL}/supplier-products`, { headers: getHeaders(), params }),
  createSupplierProduct: (data) => axios.post(`${API_URL}/supplier-products`, data, { headers: getHeaders() }),
  updateSupplierProduct: (id, data) => axios.put(`${API_URL}/supplier-products/${id}`, data, { headers: getHeaders() }),
  deleteSupplierProduct: (id) => axios.delete(`${API_URL}/supplier-products/${id}`, { headers: getHeaders() }),
  // Product prices
  getProductPrices: (productId) => axios.get(`${API_URL}/supplier-products/${productId}/prices`, { headers: getHeaders() }),
  addProductPrice: (productId, data) => axios.post(`${API_URL}/supplier-products/${productId}/prices`, data, { headers: getHeaders() }),
  deleteProductPrice: (priceId) => axios.delete(`${API_URL}/supplier-product-prices/${priceId}`, { headers: getHeaders() }),
  // Supplier life events
  getSupplierLifeEvents: (supplierId) => axios.get(`${API_URL}/suppliers/${supplierId}/life-events`, { headers: getHeaders() }),
  createSupplierLifeEvent: (supplierId, data) => axios.post(`${API_URL}/suppliers/${supplierId}/life-events`, data, { headers: getHeaders() }),
  updateSupplierLifeEvent: (eventId, data) => axios.put(`${API_URL}/supplier-life-events/${eventId}`, data, { headers: getHeaders() }),
  deleteSupplierLifeEvent: (eventId) => axios.delete(`${API_URL}/supplier-life-events/${eventId}`, { headers: getHeaders() }),

  // Employee life events (événements marquants)
  getEmployeeLifeEvents: (employeeId) => axios.get(`${API_URL}/employees/${employeeId}/life-events`, { headers: getHeaders() }),
  createEmployeeLifeEvent: (employeeId, data) => axios.post(`${API_URL}/employees/${employeeId}/life-events`, data, { headers: getHeaders() }),
  updateEmployeeLifeEvent: (eventId, data) => axios.put(`${API_URL}/life-events/${eventId}`, data, { headers: getHeaders() }),
  deleteEmployeeLifeEvent: (eventId) => axios.delete(`${API_URL}/life-events/${eventId}`, { headers: getHeaders() }),

  // Employee salary history
  getEmployeeSalaries: (empId) => axios.get(`${API_URL}/employees/${empId}/salaries`, { headers: getHeaders() }),
  createEmployeeSalary: (empId, data) => axios.post(`${API_URL}/employees/${empId}/salaries`, data, { headers: getHeaders() }),
  updateEmployeeSalary: (id, data) => axios.put(`${API_URL}/salaries/${id}`, data, { headers: getHeaders() }),
  deleteEmployeeSalary: (id) => axios.delete(`${API_URL}/salaries/${id}`, { headers: getHeaders() }),

  // Subcontractor life events + order assignment
  getSubcontractorLifeEvents: (subId) => axios.get(`${API_URL}/subcontractors/${subId}/life-events`, { headers: getHeaders() }),
  createSubcontractorLifeEvent: (subId, data) => axios.post(`${API_URL}/subcontractors/${subId}/life-events`, data, { headers: getHeaders() }),
  updateSubcontractorLifeEvent: (eventId, data) => axios.put(`${API_URL}/subcontractor-life-events/${eventId}`, data, { headers: getHeaders() }),
  deleteSubcontractorLifeEvent: (eventId) => axios.delete(`${API_URL}/subcontractor-life-events/${eventId}`, { headers: getHeaders() }),
  assignOrderToSubcontractor: (subId, data) => axios.post(`${API_URL}/subcontractors/${subId}/assign-order`, data, { headers: getHeaders() }),
  unassignOrderFromSubcontractor: (subId, orderId) => axios.post(`${API_URL}/subcontractors/${subId}/unassign-order/${orderId}`, {}, { headers: getHeaders() }),
  getSubcontractorReliability: (subId) => axios.get(`${API_URL}/subcontractors/${subId}/reliability`, { headers: getHeaders() }),

  // User table preferences (sync col visibility / order / sort server-side per user)
  getAllUserTablePrefs: () => axios.get(`${API_URL}/user-table-prefs`, { headers: getHeaders() }),
  getUserTablePref: (tableKey) => axios.get(`${API_URL}/user-table-prefs/${tableKey}`, { headers: getHeaders() }),
  upsertUserTablePref: (tableKey, payload) => axios.put(`${API_URL}/user-table-prefs/${tableKey}`, payload, { headers: getHeaders() }),
  deleteUserTablePref: (tableKey) => axios.delete(`${API_URL}/user-table-prefs/${tableKey}`, { headers: getHeaders() }),

  // Disputes (Litiges)
  getDisputes: (params = {}) => axios.get(`${API_URL}/disputes`, { headers: getHeaders(), params }),
  getDispute: (id) => axios.get(`${API_URL}/disputes/${id}`, { headers: getHeaders() }),
  createDispute: (data) => axios.post(`${API_URL}/disputes`, data, { headers: getHeaders() }),
  updateDispute: (id, data) => axios.patch(`${API_URL}/disputes/${id}`, data, { headers: getHeaders() }),
  deleteDispute: (id) => axios.delete(`${API_URL}/disputes/${id}`, { headers: getHeaders() }),
  getActiveDisputesByThirdParty: () => axios.get(`${API_URL}/disputes/active-by-third-party`, { headers: getHeaders() }),
  addDisputeHistory: (id, formData) => axios.post(`${API_URL}/disputes/${id}/history`, formData, {
    headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
  }),
  deleteDisputeHistory: (disputeId, entryId) => axios.delete(`${API_URL}/disputes/${disputeId}/history/${entryId}`, { headers: getHeaders() }),
  getDisputeAttachmentUrl: (fileId) => `${API_URL}/disputes/attachments/${fileId}`,


  // Archive / unarchive
  archiveSeller: (id) => axios.post(`${API_URL}/sellers/${id}/archive`, {}, { headers: getHeaders() }),
  unarchiveSeller: (id) => axios.post(`${API_URL}/sellers/${id}/unarchive`, {}, { headers: getHeaders() }),
  archiveSupplier: (id) => axios.post(`${API_URL}/suppliers/${id}/archive`, {}, { headers: getHeaders() }),
  unarchiveSupplier: (id) => axios.post(`${API_URL}/suppliers/${id}/unarchive`, {}, { headers: getHeaders() }),
  archiveSubcontractor: (id) => axios.post(`${API_URL}/subcontractors/${id}/archive`, {}, { headers: getHeaders() }),
  unarchiveSubcontractor: (id) => axios.post(`${API_URL}/subcontractors/${id}/unarchive`, {}, { headers: getHeaders() }),

  // Employee ↔ seller linking: commissions history via linked seller
  getEmployeeCommissions: (employeeId, params) => axios.get(`${API_URL}/employees/${employeeId}/commissions`, { headers: getHeaders(), params }),
  getEmployeePayslip: (employeeId, params) => axios.get(`${API_URL}/employees/${employeeId}/payslip`, { headers: getHeaders(), params }),
  getEmployeeTimesheet: (employeeId, params) => axios.get(`${API_URL}/employees/${employeeId}/timesheet`, { headers: getHeaders(), params }),
  archiveEquipment: (id) => axios.post(`${API_URL}/equipment/${id}/archive`, {}, { headers: getHeaders() }),
  unarchiveEquipment: (id) => axios.post(`${API_URL}/equipment/${id}/unarchive`, {}, { headers: getHeaders() }),

  // Suppliers bulk import
  previewSuppliersImport: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API_URL}/import/suppliers/preview`, fd, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
  },
  confirmSuppliersImport: (payload) => axios.post(`${API_URL}/import/suppliers/confirm`, payload, { headers: getHeaders() }),
  rollbackSuppliersImport: (batchId) => axios.post(`${API_URL}/import/suppliers/${batchId}/rollback`, {}, { headers: getHeaders() }),

  // Supplier analytics (price alerts + savings opportunities)
  getPriceAlerts: (thresholdPct) => axios.get(`${API_URL}/supplier-analytics/price-alerts`, { headers: getHeaders(), params: { threshold_pct: thresholdPct } }),
  getSavingsOpportunities: (minPct) => axios.get(`${API_URL}/supplier-analytics/savings-opportunities`, { headers: getHeaders(), params: { min_savings_pct: minPct } }),
  getProductAlternatives: (productId) => axios.get(`${API_URL}/supplier-analytics/products/${productId}/alternatives`, { headers: getHeaders() }),

  // Organizations (P2/P3 multi-tenant)
  getMyOrgs: () => axios.get(`${API_URL}/orgs/me`, { headers: getHeaders() }),
  getRoleTemplates: () => axios.get(`${API_URL}/orgs/role-templates`, { headers: getHeaders() }),
  setActiveOrg: (organization_id) => axios.post(`${API_URL}/orgs/me/set-active`, { organization_id }, { headers: getHeaders() }),
  getOrg: (id) => axios.get(`${API_URL}/orgs/${id}`, { headers: getHeaders() }),
  updateOrg: (id, data) => axios.patch(`${API_URL}/orgs/${id}`, data, { headers: getHeaders() }),
  getOrgMembers: (id) => axios.get(`${API_URL}/orgs/${id}/members`, { headers: getHeaders() }),
  inviteOrgMember: (id, data) => axios.post(`${API_URL}/orgs/${id}/invite`, data, { headers: getHeaders() }),
  resendOrgInvitation: (id, invId) => axios.post(`${API_URL}/orgs/${id}/invitations/${invId}/resend`, {}, { headers: getHeaders() }),
  deleteOrgInvitation: (id, invId) => axios.delete(`${API_URL}/orgs/${id}/invitations/${invId}`, { headers: getHeaders() }),

  // Field requests (Demandes équipes chantier)
  getFieldRequests: (params) => axios.get(`${API_URL}/field-requests`, { headers: getHeaders(), params }),
  createFieldRequest: (data) => axios.post(`${API_URL}/field-requests`, data, { headers: getHeaders() }),
  updateFieldRequest: (id, data) => axios.patch(`${API_URL}/field-requests/${id}`, data, { headers: getHeaders() }),
  deleteFieldRequest: (id) => axios.delete(`${API_URL}/field-requests/${id}`, { headers: getHeaders() }),
  addFieldRequestComment: (id, text) => axios.post(`${API_URL}/field-requests/${id}/comment`, { text }, { headers: getHeaders() }),
  deleteFieldRequestComment: (id, commentId) => axios.delete(`${API_URL}/field-requests/${id}/comment/${commentId}`, { headers: getHeaders() }),

  // Employee submissions (Fiches salarié : heures + absences auto-déclarées)
  getEmployeeSubmissions: (params) => axios.get(`${API_URL}/employee-submissions`, { headers: getHeaders(), params }),
  updateEmployeeSubmission: (id, data) => axios.patch(`${API_URL}/employee-submissions/${id}`, data, { headers: getHeaders() }),
  getEmployeeWorkEntries: (employeeId, params) => axios.get(`${API_URL}/employees/${employeeId}/work-entries`, { headers: getHeaders(), params }),
  submitHoursRequest: (data) => axios.post(`${API_URL}/employee-submissions/hours`, data, { headers: getHeaders() }),
  submitAbsenceRequest: (data) => axios.post(`${API_URL}/employee-submissions/absence`, data, { headers: getHeaders() }),
  approveSubmission: (id, data) => axios.post(`${API_URL}/employee-submissions/${id}/approve`, data, { headers: getHeaders() }),
  rejectSubmission: (id, data) => axios.post(`${API_URL}/employee-submissions/${id}/reject`, data, { headers: getHeaders() }),
  deleteSubmission: (id) => axios.delete(`${API_URL}/employee-submissions/${id}`, { headers: getHeaders() }),
  acceptOrgInvite: (data) => axios.post(`${API_URL}/orgs/accept-invite`, data),
  updateOrgMember: (id, user_id, data) => axios.patch(`${API_URL}/orgs/${id}/members/${user_id}`, data, { headers: getHeaders() }),
  removeOrgMember: (id, user_id) => axios.delete(`${API_URL}/orgs/${id}/members/${user_id}`, { headers: getHeaders() }),
  buyOrgSlots: (id, additional_slots) => axios.post(`${API_URL}/orgs/${id}/buy-slots`, { additional_slots }, { headers: getHeaders() }),

  // Stripe payments (P5)
  createSlotsCheckout: (org_id, slots, origin_url, billing_period = "monthly") =>
    axios.post(`${API_URL}/payments/checkout/slots`, { org_id, slots, origin_url, billing_period }, { headers: getHeaders() }),
  createWorkerSlotsCheckout: (org_id, slots, origin_url) =>
    axios.post(`${API_URL}/payments/checkout/worker-slots`, { org_id, slots, origin_url }, { headers: getHeaders() }),
  getCheckoutStatus: (session_id) =>
    axios.get(`${API_URL}/payments/checkout/status/${session_id}`, { headers: getHeaders() }),

  // Super admin (P6)
  superAdminListOrgs: () => axios.get(`${API_URL}/super-admin/orgs`, { headers: getHeaders() }),
  superAdminStats: () => axios.get(`${API_URL}/super-admin/stats`, { headers: getHeaders() }),
  superAdminSuspendOrg: (id, reason) => axios.post(`${API_URL}/super-admin/orgs/${id}/suspend`, { reason }, { headers: getHeaders() }),
  superAdminReactivateOrg: (id) => axios.post(`${API_URL}/super-admin/orgs/${id}/reactivate`, {}, { headers: getHeaders() }),
  superAdminChangePlan: (id, plan) => axios.patch(`${API_URL}/super-admin/orgs/${id}/plan`, { plan }, { headers: getHeaders() }),
  superAdminDeleteOrg: (id, confirm_phrase) => axios.delete(`${API_URL}/super-admin/orgs/${id}`, { headers: getHeaders(), data: { confirm_phrase } }),

  // Billing (transactions, invoices, cancellation)
  getBillingHistory: (org_id) => axios.get(`${API_URL}/orgs/${org_id}/billing-history`, { headers: getHeaders() }),
  getInvoiceData: (org_id, tx_id) => axios.get(`${API_URL}/orgs/${org_id}/billing-history/${tx_id}/invoice`, { headers: getHeaders() }),
  cancelSubscription: (org_id, reason) => axios.post(`${API_URL}/orgs/${org_id}/cancel-subscription`, { reason, confirm: true }, { headers: getHeaders() }),
  resumeSubscription: (org_id) => axios.post(`${API_URL}/orgs/${org_id}/resume-subscription`, {}, { headers: getHeaders() }),

  // Analytics events (funnel tracking)
  trackEvent: (event, metadata = {}, org_id = null) =>
    axios.post(`${API_URL}/analytics/track`, { event, metadata, org_id }, { headers: getHeaders() }).catch(() => null),
  getFunnel: () => axios.get(`${API_URL}/analytics/funnel`, { headers: getHeaders() }),
};

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR"
  }).format(amount);
};

export const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("fr-FR");
};

/**
 * Convert a Date object to a "YYYY-MM-DD" string using LOCAL time.
 * Prefer this over `d.toISOString().split("T")[0]` which converts to UTC
 * and shifts dates by one day in positive-offset timezones.
 */
export const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const formatPercent = (value) => {
  return `${(value * 100).toFixed(1)}%`;
};

export const statusLabels = {
  pending: "En attente",
  planning: "Au planning",
  in_progress: "En cours",
  ready: "Prête",
  completed: "Terminée",
  cashed: "Encaissée",
  cancelled: "Annulée"
};

// Convert axios error (especially FastAPI 422 validation) into a displayable string
export const getErrorMessage = (err, fallback = "Erreur") => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => (typeof d === "string" ? d : (d?.msg || JSON.stringify(d)))).join(", ");
  }
  return JSON.stringify(detail);
};

export const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  planning: "bg-purple-100 text-purple-800",
  in_progress: "bg-blue-100 text-blue-800",
  ready: "bg-emerald-100 text-emerald-800",
  completed: "bg-emerald-200 text-emerald-900",
  cashed: "bg-green-200 text-green-900",
  cancelled: "bg-red-100 text-red-800"
};

// Logistics — Phase 177
export const logisticsApi = {
  // Categories
  getLogisticsCategories: () => axios.get(`${API_URL}/logistics-categories`, { headers: getHeaders() }),
  createLogisticsCategory: (data) => axios.post(`${API_URL}/logistics-categories`, data, { headers: getHeaders() }),
  updateLogisticsCategory: (id, data) => axios.patch(`${API_URL}/logistics-categories/${id}`, data, { headers: getHeaders() }),
  deleteLogisticsCategory: (id) => axios.delete(`${API_URL}/logistics-categories/${id}`, { headers: getHeaders() }),
  // Catalog
  getLogisticsCatalog: () => axios.get(`${API_URL}/logistics-catalog`, { headers: getHeaders() }),
  createLogisticsCatalogItem: (data) => axios.post(`${API_URL}/logistics-catalog`, data, { headers: getHeaders() }),
  updateLogisticsCatalogItem: (id, data) => axios.patch(`${API_URL}/logistics-catalog/${id}`, data, { headers: getHeaders() }),
  deleteLogisticsCatalogItem: (id) => axios.delete(`${API_URL}/logistics-catalog/${id}`, { headers: getHeaders() }),
  // Per-order items
  addOrderLogisticsItem: (orderId, data) => axios.post(`${API_URL}/orders/${orderId}/logistics-items`, data, { headers: getHeaders() }),
  updateOrderLogisticsItem: (orderId, itemId, data) => axios.patch(`${API_URL}/orders/${orderId}/logistics-items/${itemId}`, data, { headers: getHeaders() }),
  deleteOrderLogisticsItem: (orderId, itemId) => axios.delete(`${API_URL}/orders/${orderId}/logistics-items/${itemId}`, { headers: getHeaders() }),
  // Aggregations
  getLogisticsTodo: () => axios.get(`${API_URL}/logistics-todo`, { headers: getHeaders() }),
  getLogisticsOverdue: () => axios.get(`${API_URL}/logistics-overdue`, { headers: getHeaders() }),
  getOrdersLogisticsSummary: () => axios.get(`${API_URL}/orders-logistics-summary`, { headers: getHeaders() }),
  // Description Rich Text
  updateOrderDescription: (orderId, description_html) =>
    axios.patch(`${API_URL}/orders/${orderId}/description`, { description_html }, { headers: getHeaders() }),
  // Media
  listOrderMedia: (orderId) => axios.get(`${API_URL}/orders/${orderId}/media`, { headers: getHeaders() }),
  uploadOrderMedia: (orderId, file, { title, comment, show_in_worksheet } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    const params = {};
    if (title) params.title = title;
    if (comment) params.comment = comment;
    if (show_in_worksheet !== undefined) params.show_in_worksheet = show_in_worksheet;
    return axios.post(`${API_URL}/orders/${orderId}/media`, fd, {
      headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      params,
    });
  },
  updateOrderMedia: (orderId, mediaId, data) =>
    axios.patch(`${API_URL}/orders/${orderId}/media/${mediaId}`, data, { headers: getHeaders() }),
  deleteOrderMedia: (orderId, mediaId) =>
    axios.delete(`${API_URL}/orders/${orderId}/media/${mediaId}`, { headers: getHeaders() }),
  getOrderMediaFileUrl: (orderId, mediaId) =>
    `${API_URL}/orders/${orderId}/media/${mediaId}/file`,
  fetchOrderMediaBlob: (orderId, mediaId) =>
    axios.get(`${API_URL}/orders/${orderId}/media/${mediaId}/file`, {
      headers: getHeaders(), responseType: "blob",
    }),
  // Worksheet PDF
  getOrderWorksheetPdf: (orderId) =>
    axios.get(`${API_URL}/orders/${orderId}/worksheet-pdf`, {
      headers: getHeaders(), responseType: "blob",
    }),
};

// Product equivalence groups + Order type material templates
export const equivalencesApi = {
  listGroups: () => axios.get(`${API_URL}/product-equivalence-groups`, { headers: getHeaders() }),
  getGroup: (id) => axios.get(`${API_URL}/product-equivalence-groups/${id}`, { headers: getHeaders() }),
  createGroup: (data) => axios.post(`${API_URL}/product-equivalence-groups`, data, { headers: getHeaders() }),
  updateGroup: (id, data) => axios.put(`${API_URL}/product-equivalence-groups/${id}`, data, { headers: getHeaders() }),
  deleteGroup: (id) => axios.delete(`${API_URL}/product-equivalence-groups/${id}`, { headers: getHeaders() }),
  getGroupStockAvailability: (id) =>
    axios.get(`${API_URL}/product-equivalence-groups/${id}/stock-availability`, { headers: getHeaders() }),
  listTemplates: (orderTypeId) =>
    axios.get(`${API_URL}/order-types/${orderTypeId}/material-templates`, { headers: getHeaders() }),
  createTemplate: (orderTypeId, data) =>
    axios.post(`${API_URL}/order-types/${orderTypeId}/material-templates`, data, { headers: getHeaders() }),
  updateTemplate: (templateId, data) =>
    axios.put(`${API_URL}/order-type-material-templates/${templateId}`, data, { headers: getHeaders() }),
  deleteTemplate: (templateId) =>
    axios.delete(`${API_URL}/order-type-material-templates/${templateId}`, { headers: getHeaders() }),
  getOrderTemplates: (orderId) =>
    axios.get(`${API_URL}/orders/${orderId}/material-templates`, { headers: getHeaders() }),
  reorderTemplates: (orderTypeId, ids) =>
    axios.post(`${API_URL}/order-types/${orderTypeId}/material-templates/reorder`, { ids }, { headers: getHeaders() }),
  distributeGroupAcrossSuppliers: (orderId, data) =>
    axios.post(`${API_URL}/orders/${orderId}/materials/distribute-group`, data, { headers: getHeaders() }),
};

export const monthNames = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];
